import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { galleryApi, resolveImageUrl, type GalleryPost } from "@/lib/api";
import { formatDate } from "@/lib/date";
import ScrollReveal from "@/components/ScrollReveal";

type Work = Omit<GalleryPost, "created_at"> & {
  date: string;
};

const categories = ["전체", "현장", "제품", "품질", "정비", "특수", "샘플"];

const categoryBadge: Record<string, string> = {
  현장: "bg-accent-100 text-accent-900",
  제품: "bg-primary-100 text-primary-900",
  품질: "bg-secondary-100 text-secondary-900",
  정비: "bg-foreground-100 text-foreground-900",
  특수: "bg-primary-100 text-primary-900",
  샘플: "bg-accent-100 text-accent-900",
};

const defaultDescriptions: Record<string, string> = {
  현장: "해당 현장에서의 작업 상황과 결과물입니다. 요청하신 사이즈와 규격에 맞춰 칩핑 작업을 진행했으며, 현장 여건에 따른 최적의 작업 방식으로 완료했습니다.",
  제품: "생산된 원목칩/가지칩의 실제 결과물 사진입니다. 고객님 요청 사양에 맞춰 균일한 품질로 생산되었으며, 샘플 확인 후 대량 생산 진행 가능합니다.",
  품질: "품질 관리 과정 중 촬영한 사진입니다. 칩 크기, 수분 함량, 이물질 여부 등 다각도로 품질 점검을 진행하고 있으며, 고객 요구 품질 기준을 준수합니다.",
  정비: "장비 정비 및 부품 교체 작업 기록입니다. 정기적인 점검과 신속한 정비를 통해 현장 작업 중 다운타임을 최소화하고 안전한 작업 환경을 유지합니다.",
  특수: "일반적인 원목 외에도 다양한 형태의 목재를 파쇄할 수 있는 작업 사례입니다. 뿌리, 굵은 가지, 건설 폐목 등 특수 상황도 상담 후 처리 가능합니다.",
  샘플: "고객님께서 요청하신 스크린 사이즈별 샘플 생산 결과물입니다. 40×40, 60×60 등 원하시는 규격에 맞춰 샘플을 먼저 제공해 드리고 있으며, 확인 후 본 작업 진행합니다.",
};

function mapRowToWork(row: GalleryPost): Work {
  return {
    id: row.id,
    title: row.title,
    date: formatDate(row.created_at),
    category: row.category,
    views: row.views,
    image: row.image,
    content: row.content,
    author: row.author,
  };
}

function GalleryDetailModal({
  work,
  onClose,
}: {
  work: Work;
  onClose: () => void;
}) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-16 md:pt-24 px-4 bg-background-50/95 backdrop-blur-sm">
      <div
        className="relative w-full max-w-3xl max-h-[85vh] bg-background-50 rounded-xl border border-background-200 shadow-xl overflow-hidden flex flex-col animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 모달 헤더 */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-background-200">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${categoryBadge[work.category] || "bg-background-200 text-foreground-700"}`}
          >
            {work.category}
          </span>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
          >
            <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
          </button>
        </div>

        {/* 모달 본문 (스크롤) */}
        <div className="flex-1 overflow-y-auto">
          {/* 이미지 */}
          <div className="relative w-full h-64 md:h-80 overflow-hidden bg-background-100 flex items-center justify-center">
            <img
              src={resolveImageUrl(work.image)}
              alt={`${work.title} 작업 이미지`}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* 콘텐츠 */}
          <div className="p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {work.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {work.author || "정대성"}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                조회 {work.views}
              </span>
            </div>

            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              {work.title}
            </h3>

            <div className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
              {work.content || defaultDescriptions[work.category] || ""}
            </div>
          </div>
        </div>

        {/* 모달 푸터 */}
        <div className="shrink-0 flex items-center justify-end px-6 py-4 border-t border-background-200">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Gallery() {
  const navigate = useNavigate();
  const [active, setActive] = useState("전체");
  const [works, setWorks] = useState<Work[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedWork, setSelectedWork] = useState<Work | null>(null);

  const fetchWorks = useCallback(async (category: string) => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await galleryApi.list(category);
      setWorks((data || []).map(mapRowToWork));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "알 수 없는 오류";
      setLoadError(message);
      setWorks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorks(active);
  }, [active, fetchWorks]);

  const handleCategoryChange = (cat: string) => {
    setActive(cat);
  };

  const handleCardClick = async (w: Work) => {
    setSelectedWork(w);

    try {
      await galleryApi.incrementView(w.id);
    } catch {
      // 조회수 반영 실패는 무시
    }

    setWorks((prev) =>
      prev.map((p) => (p.id === w.id ? { ...p, views: p.views + 1 } : p))
    );
    setSelectedWork((prev) =>
      prev ? { ...prev, views: prev.views + 1 } : null
    );
  };

  const handleCloseModal = () => {
    setSelectedWork(null);
  };

  const filtered =
    active === "전체" ? works : works.filter((w) => w.category === active);

  return (
    <section id="gallery" className="relative py-24 md:py-32 bg-background-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div className="max-w-2xl">
            <ScrollReveal direction="up" delay={100}>
              <div className="inline-flex items-center gap-2 text-primary-700 text-xs tracking-[0.3em] uppercase font-medium mb-5">
                <span className="w-8 h-px bg-primary-500"></span>
                Work Gallery
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight">
                현장에서 만든 <span className="text-primary-700">이야기</span>
              </h2>
              <p className="mt-6 text-foreground-700 text-base md:text-lg leading-relaxed">
                작업 현장의 순간과 생산된 원목칩·가지칩의 실제 결과물입니다.
                규모, 조건, 요구 사이즈가 다양한 사례를 통해 우공이산의 작업
                방식을 확인해 보세요.
              </p>
            </ScrollReveal>
          </div>

          <ScrollReveal direction="up" delay={200}>
            <div className="flex flex-wrap gap-2 px-1 py-1 rounded-full bg-background-100 border border-background-200/70">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => handleCategoryChange(c)}
                  className={`px-4 py-2 text-xs md:text-sm rounded-full font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    active === c
                      ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                      : "text-foreground-700 hover:text-primary-700"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </ScrollReveal>
        </div>

        {loading && (
          <div className="mt-14 py-20 text-center text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-2xl w-8 h-8 flex items-center justify-center mx-auto mb-3"></i>
            <span className="text-sm">작업 사례를 불러오는 중...</span>
          </div>
        )}

        {loadError && !loading && filtered.length === 0 && (
          <div className="mt-14 py-20 text-center">
            <div className="inline-flex flex-col items-center gap-3">
              <i className="ri-error-warning-line text-3xl w-10 h-10 flex items-center justify-center text-accent-600"></i>
              <span className="text-sm text-foreground-600">{loadError}</span>
              <button
                onClick={() => fetchWorks(active)}
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors"
              >
                다시 시도
              </button>
            </div>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((w) => (
              <article
                key={w.id}
                onClick={() => handleCardClick(w)}
                className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-500 cursor-pointer"
              >
                <div className="relative w-full h-64 overflow-hidden">
                  <img
                    src={resolveImageUrl(w.image)}
                    alt={`${w.title} 작업 이미지`}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-background-50/95 backdrop-blur text-xs font-medium text-foreground-900">
                      #{w.category}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1 rounded-full bg-foreground-950/60 backdrop-blur text-xs text-background-50">
                    <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                    {w.views}
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center justify-between text-xs text-foreground-500 mb-3">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {w.date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {w.author || "정대성"}
                    </span>
                  </div>
                  <h4 className="font-heading text-lg font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {w.title}
                  </h4>
                </div>
              </article>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && !loadError && (
          <div className="mt-14 py-16 text-center text-foreground-500 text-sm">
            <i className="ri-folder-open-line w-8 h-8 flex items-center justify-center mx-auto mb-3 text-foreground-300"></i>
            해당 분류의 작업 사례가 없습니다.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mt-14 flex justify-center">
            <button
              onClick={() => navigate("/admin/gallery")}
              className="px-8 py-4 rounded-md border border-background-300 hover:border-primary-500 hover:bg-primary-50 text-foreground-800 hover:text-primary-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              더 많은 작업 보기
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </button>
          </div>
        )}
      </div>

      {selectedWork && (
        <GalleryDetailModal work={selectedWork} onClose={handleCloseModal} />
      )}
    </section>
  );
}